/**
 * @fileoverview Per-agent detail dispatcher.
 *
 * Given a task's `agentId`, renders the appropriate detail view component.
 * Extracted from `SummarizationView` so the in-page navigation flow and the
 * slug-based route (`/summaries/$agent`) can share the same dispatch table.
 *
 * Add new agents here once their detail view component exists; otherwise the
 * default branch falls through to the generic `SummarizationAgentDetail`.
 */

import type { Id } from "@cvx/_generated/dataModel";
import { CorporateStructureView } from "./corporate";
import { EmploymentView } from "./employment";
import { AssetListView } from "./assetList";
import { SecurityInterestsView } from "./securityInterests";
import { GeneralLiabilitiesView } from "./generalLiabilities";
import { BoardReportsView } from "./boardReports";
import { LoansSummaryView } from "./loansSummary";
import { AssetSalesPurchasesView } from "./assetSalesPurchases";
import { IndicatorView } from "./indicator";
import { BankTransactionsView } from "./bankTransactions";
import { RelatedPartiesView } from "./relatedParties";
import { FinancialAnalysisView } from "./financialAnalysis";
import { AtoSroView } from "./atoSro";
import { SummarizationAgentDetail } from "./SummarizationAgentDetail";
import type {
  FinancialAnalysisContext,
  FinancialAnalysisSection,
} from "./financialAnalysis/types";

export interface AgentDetailRouterProps {
  agentId: string;
  taskId: Id<"summarizationTasks">;
  jobId: Id<"jobs">;
  summarizationRunId: Id<"summarizationRuns">;
  onBack: () => void;
  onRerun: () => void;
  isViewer?: boolean;
  /**
   * Financial-analysis destination and its route-carried context (MOR-1280);
   * other agents ignore these.
   */
  financialSection?: FinancialAnalysisSection;
  financialContext?: FinancialAnalysisContext;
  onFinancialContextChange?: (patch: FinancialAnalysisContext) => void;
  onOpenFinancialSection?: (
    section: FinancialAnalysisSection,
    context: FinancialAnalysisContext,
  ) => void;
}

/**
 * Picks the detail view for an agent's summarization task. Financial
 * analysis gets its section, context, and navigation callbacks; every other
 * agent receives only the shared detail props.
 */
export function AgentDetailRouter(props: AgentDetailRouterProps) {
  const {
    agentId,
    financialSection,
    financialContext,
    onFinancialContextChange,
    onOpenFinancialSection,
    ...detailProps
  } = props;

  switch (agentId) {
    case "sum_corporate":
      return <CorporateStructureView {...detailProps} />;
    case "sum_employment":
      return <EmploymentView {...detailProps} />;
    case "sum_asset_list":
      return <AssetListView {...detailProps} />;
    case "sum_bank_transactions":
      return <BankTransactionsView {...detailProps} />;
    case "sum_general_liabilities":
      return <GeneralLiabilitiesView {...detailProps} />;
    case "sum_board_reports":
      return <BoardReportsView {...detailProps} />;
    case "sum_loans":
      return <LoansSummaryView {...detailProps} />;
    case "sum_asset_sales":
      return <AssetSalesPurchasesView {...detailProps} />;
    case "sum_security_interests":
      return <SecurityInterestsView {...detailProps} />;
    case "sum_indicator":
      return <IndicatorView {...detailProps} />;
    case "sum_related_parties":
      return <RelatedPartiesView {...detailProps} />;
    case "sum_financial_analysis":
      return (
        <FinancialAnalysisView
          {...detailProps}
          section={financialSection}
          context={financialContext}
          onContextChange={onFinancialContextChange}
          onOpenSection={onOpenFinancialSection}
        />
      );
    case "sum_ato_sro":
      return <AtoSroView {...detailProps} />;
    default:
      return <SummarizationAgentDetail {...detailProps} />;
  }
}
